import React, { createContext, useContext, useReducer, useEffect, useCallback, useState, useRef } from 'react';
import leadService from '../api/crm/leads/leadService';
import { get } from '../api/apiClient';
import { API_ENDPOINTS } from '../api/config';


// Lead Context for managing lead data
const LeadContext = createContext();

// Lead reducer for state management
const leadReducer = (state, action) => {
  switch (action.type) {
    case 'SET_LEADS':
      return {
        ...state,
        leads: action.payload,
        loading: false
      };
    case 'SET_PAGINATION':
      return {
        ...state,
        pagination: action.payload
      };
    case 'ADD_LEAD':
      return {
        ...state,
        leads: [action.payload, ...state.leads],
        loading: false
      };
    case 'UPDATE_LEAD':
      return {
        ...state,
        leads: state.leads.map(lead =>
          (lead._id === action.payload._id || lead.id === action.payload.id || lead.leadId === action.payload.leadId)
            ? action.payload
            : lead
        ),
        loading: false
      };
    case 'DELETE_LEAD':
      return {
        ...state,
        leads: state.leads.filter(lead =>
          lead._id !== action.payload &&
          lead.id !== action.payload &&
          lead.leadId !== action.payload
        ),
        loading: false
      };
    case 'BULK_DELETE_LEADS':
      return {
        ...state,
        leads: state.leads.filter(lead => !action.payload.includes(lead._id || lead.id || lead.leadId)),
        loading: false
      };
    case 'SET_LOADING':
      return {
        ...state,
        loading: action.payload
      };
    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
        loading: false
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null
      };
    default:
      return state;
  }
};

// Initial state
const initialState = {
  leads: [],
  loading: false,
  error: null,
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalLeads: 0,
    hasNextPage: false,
    hasPrevPage: false,
    limit: 10
  }
};

// Lead Context Provider
export const LeadProvider = ({ children }) => {
  const [state, dispatch] = useReducer(leadReducer, initialState);
  const [leadStatuses, setLeadStatuses] = useState([]);
  const leadStatusesRef = useRef([]);

  useEffect(() => {
    leadStatusesRef.current = leadStatuses;
  }, [leadStatuses]);

  useEffect(() => {
    const fetchStatuses = async () => {
      try {
        const result = await get(`${API_ENDPOINTS.CRM.CONFIG.LEAD_STATUSES_DROPDOWN}`);
        if (result.success) {
          setLeadStatuses(result.data || []);
        }
      } catch (error) {
      }
    };
    fetchStatuses();
  }, []);

  const mapStageToStatusName = useCallback((stage) => {
    if (!stage) return '';
    const found = leadStatusesRef.current.find(s => s.value === stage);
    return found ? found.name : stage.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  }, []);


  // Load leads from API - memoized to prevent infinite loops
  const loadLeads = useCallback(async (filters = {}) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });

      const result = await leadService.getAllLeads(filters);

      if (result.success) {
        if (result.leads && Array.isArray(result.leads)) {
          const mappedLeads = result.leads.map(lead => {
            try {
              const mapped = mapLeadToFrontend(lead);
              if (!mapped) {
                return null;
              }
              return mapped;
            } catch (mapError) {
              return null;
            }
          }).filter(lead => lead !== null);

          dispatch({ type: 'SET_LEADS', payload: mappedLeads });
          dispatch({ type: 'CLEAR_ERROR' });

          if (result.pagination) {
            const backendPagination = result.pagination;
            const normalizedPagination = {
              currentPage: backendPagination.currentPage || backendPagination.page || 1,
              totalPages: backendPagination.totalPages || backendPagination.pages || 1,
              totalLeads: backendPagination.totalLeads || backendPagination.total || 0,
              hasNextPage: backendPagination.hasNextPage !== undefined
                ? backendPagination.hasNextPage
                : (backendPagination.currentPage || backendPagination.page || 1) < (backendPagination.totalPages || backendPagination.pages || 1),
              hasPrevPage: backendPagination.hasPrevPage !== undefined
                ? backendPagination.hasPrevPage
                : (backendPagination.currentPage || backendPagination.page || 1) > 1,
              limit: backendPagination.limit || 10
            };
            dispatch({ type: 'SET_PAGINATION', payload: normalizedPagination });
          }
        } else {
          dispatch({ type: 'SET_LEADS', payload: [] });
        }
      } else {
        dispatch({ type: 'SET_ERROR', payload: result.error || 'Failed to load leads' });
        dispatch({ type: 'SET_LEADS', payload: [] });
      }
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error.message || 'Failed to load leads' });
      dispatch({ type: 'SET_LEADS', payload: [] });
    }
  }, []); // Empty dependency array - function doesn't depend on any props or state

  // NOTE:
  // We intentionally do NOT auto-load leads here.
  // Screens that need leads data should call `loadLeads` explicitly via `useLeads()`.

  // Map backend lead to frontend format
  const mapLeadToFrontend = (lead) => {
    if (!lead) {
      return null;
    }

    return {
      id: lead._id || lead.leadId || lead.id,
      leadId: lead.leadId,
      firstName: lead.firstName,
      lastName: lead.lastName,
      email: lead.email,
      phone: lead.phone,
      alternatePhone: lead.alternatePhone,
      company: lead.company,
      website: lead.website,
      designation: lead.designation,
      source: lead.source || 'manual_entry',
      companySource: lead.companySource,
      stage: lead.stage,
      score: lead.score || 0,
      scoreLabel: lead.scoreLabel || 'cold',
      status: mapStageToStatus(lead.stage),
      region: lead.region || lead.address?.state || '',
      tags: lead.tags || [],
      address: lead.address?.street || '',
      city: lead.address?.city || '',
      state: lead.address?.state || '',
      pincode: lead.address?.pincode || '',
      country: lead.address?.country || '',
      businessType: lead.businessType,
      annualRevenue: lead.annualRevenue,
      employeeCount: lead.employeeCount,
      companySize: lead.companySize,
      companySizeId: lead.companySize?._id || (typeof lead.companySize === 'string' ? lead.companySize : ''),
      gstNumber: lead.gstNumber,
      drugLicenseNumber: lead.drugLicenseNumber,
      budget: lead.budget,
      leadValue: lead.leadValue || lead.budget || 0,
      timeline: lead.timeline,
      productRequirement: lead.productRequirement,
      notes: lead.notes,
      internalNotes: lead.internalNotes,
      companyPdf: lead.companyPdf,
      companyPdfPublicId: lead.companyPdfPublicId,
      companyDocuments: lead.companyDocuments || [],
      locations: lead.locations || [],
      createdAt: lead.createdAt,
      updatedAt: lead.updatedAt,
      createdBy: lead.createdBy && typeof lead.createdBy === 'object'
        ? `${lead.createdBy.firstName || ''} ${lead.createdBy.lastName || ''}`.trim() || lead.createdBy.email
        : lead.createdBy || 'Unknown',
      assignedTo: lead.assignedTo && typeof lead.assignedTo === 'object'
        ? `${lead.assignedTo.firstName || ''} ${lead.assignedTo.lastName || ''}`.trim() || lead.assignedTo.email
        : lead.assignedTo || 'Unassigned',
      // For backward compatibility
      name: `${lead.firstName || ''} ${lead.lastName || ''}`.trim(),
      owner: lead.assignedTo && typeof lead.assignedTo === 'object'
        ? `${lead.assignedTo.firstName || ''} ${lead.assignedTo.lastName || ''}`.trim() || lead.assignedTo.email
        : (lead.assignedTo || 'Unassigned'),
      ownerEmail: lead.assignedTo && typeof lead.assignedTo === 'object'
        ? lead.assignedTo.email
        : '',
      lastActivityDate: lead.lastActivityDate || null,
      lastActivity: lead.lastActivityDate ? formatLastActivity(lead.lastActivityDate) : '',
      industry: lead.industry,
      industryId: lead.industry?._id || (typeof lead.industry === 'string' ? lead.industry : ''),
      decisionMaker: lead.decisionMaker,
      decisionMakerId: lead.decisionMaker?._id || (typeof lead.decisionMaker === 'string' ? lead.decisionMaker : ''),
      category: lead.category,
      categoryId: lead.category?._id || (typeof lead.category === 'string' ? lead.category : ''),
      sourceId: lead.source?._id || (typeof lead.source === 'string' ? lead.source : ''),
      assignedToId: lead.assignedTo?._id || (typeof lead.assignedTo === 'string' ? lead.assignedTo : ''),
      assignmentHistory: lead.assignmentHistory || [],
      duplicateInfo: lead.duplicateInfo,
      leadVideoStatus: lead.leadVideoStatus,
      leadOrderStatus: lead.leadOrderStatus,
      leadCallStatus: lead.leadCallStatus,
      leadInterest: lead.leadInterest,
      leadVideoStatusId: lead.leadVideoStatus?._id || (typeof lead.leadVideoStatus === 'string' ? lead.leadVideoStatus : ''),
      leadOrderStatusId: lead.leadOrderStatus?._id || (typeof lead.leadOrderStatus === 'string' ? lead.leadOrderStatus : ''),
      leadCallStatusId: lead.leadCallStatus?._id || (typeof lead.leadCallStatus === 'string' ? lead.leadCallStatus : ''),
      leadInterestId: lead.leadInterest?._id || (typeof lead.leadInterest === 'string' ? lead.leadInterest : '')
    };
  };



  // Map stage to status
  const mapStageToStatus = (stage) => {
    return mapStageToStatusName(stage) || 'New';
  };

  // Format last activity
  const formatLastActivity = (dateString) => {
    if (!dateString) return 'No activity';
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
      if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
      if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
      return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
    } catch (error) {
      return 'No activity';
    }
  };

  // Create a new lead
  const createLead = async (leadData) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });

      const result = await leadService.createLead(leadData);

      if (result.success) {
        const mappedLead = mapLeadToFrontend(result.lead);
        dispatch({ type: 'ADD_LEAD', payload: mappedLead });

        await loadLeads();

        return { success: true, lead: mappedLead };
      } else {
        dispatch({ type: 'SET_LOADING', payload: false });
        return { success: false, error: result.error || 'Failed to create lead' };
      }
    } catch (error) {
      dispatch({ type: 'SET_LOADING', payload: false });
      return { success: false, error: error.message || 'Failed to create lead' };
    }
  };

  // Create a quick lead with minimal data
  const createQuickLead = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });

      const quickLeadData = {
        firstName: 'New',
        lastName: 'Lead',
        email: `lead${Date.now()}@example.com`,
        phone: '0000000000',
        company: 'New Company',
        source: 'other',
        notes: 'Auto-generated lead - Created via quick create button'
      };

      const result = await leadService.createLead(quickLeadData);

      if (result.success) {
        const mappedLead = mapLeadToFrontend(result.lead);
        dispatch({ type: 'ADD_LEAD', payload: mappedLead });
        return { success: true, lead: mappedLead };
      } else {
        dispatch({ type: 'SET_LOADING', payload: false });
        return { success: false, error: result.error || 'Failed to create lead' };
      }
    } catch (error) {
      dispatch({ type: 'SET_LOADING', payload: false });
      return { success: false, error: error.message || 'Failed to create lead' };
    }
  };

  // Update an existing lead
  const updateLead = async (leadId, updateData) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });

      const result = await leadService.updateLead(leadId, updateData);

      if (result.success) {
        const mappedLead = mapLeadToFrontend(result.lead);
        dispatch({ type: 'UPDATE_LEAD', payload: mappedLead });

        dispatch({ type: 'SET_LOADING', payload: false });

        return { success: true, lead: mappedLead };
      } else {
        dispatch({ type: 'SET_LOADING', payload: false });
        return { success: false, error: result.error || 'Failed to update lead' };
      }
    } catch (error) {
      dispatch({ type: 'SET_LOADING', payload: false });
      return { success: false, error: error.message || 'Failed to update lead' };
    }
  };

  // Delete a lead
  const deleteLead = async (leadId) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });

      const result = await leadService.deleteLead(leadId);

      if (result.success) {
        dispatch({ type: 'DELETE_LEAD', payload: leadId });
        return { success: true };
      } else {
        dispatch({ type: 'SET_LOADING', payload: false });
        return { success: false, error: result.error || 'Failed to delete lead' };
      }
    } catch (error) {
      dispatch({ type: 'SET_LOADING', payload: false });
      return { success: false, error: error.message || 'Failed to delete lead' };
    }
  };

  // Bulk delete leads
  const bulkDeleteLeads = async (leadIds) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const result = await leadService.bulkDeleteLeads(leadIds);
      if (result.success) {
        dispatch({ type: 'BULK_DELETE_LEADS', payload: leadIds });
        return { success: true, message: result.message };
      } else {
        dispatch({ type: 'SET_LOADING', payload: false });
        return { success: false, error: result.error || 'Failed to delete leads' };
      }
    } catch (error) {
      dispatch({ type: 'SET_LOADING', payload: false });
      return { success: false, error: error.message || 'Failed to delete leads' };
    }
  };

  // Bulk assign leads
  const bulkAssignLeads = async (leadIds, assigneeId) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const result = await leadService.bulkAssignLeads(leadIds, assigneeId);
      if (result.success) {
        dispatch({ type: 'SET_LOADING', payload: false });
        return { success: true, message: result.message };
      } else {
        dispatch({ type: 'SET_LOADING', payload: false });
        return { success: false, error: result.error || 'Failed to assign leads' };
      }
    } catch (error) {
      dispatch({ type: 'SET_LOADING', payload: false });
      return { success: false, error: error.message || 'Failed to assign leads' };
    }
  };

  // Get lead by ID
  const getLeadById = async (leadId) => {
    try {
      const result = await leadService.getLeadById(leadId);
      if (result.success) {
        return mapLeadToFrontend(result.lead);
      }
      return null;
    } catch (error) {
      return null;
    }
  };

  // Get leads by status
  const getLeadsByStatus = (status) => {
    return state.leads.filter(lead => lead.stage === status || lead.status === status);
  };

  // Get leads by source
  const getLeadsBySource = (source) => {
    return state.leads.filter(lead => lead.source === source);
  };

  // Search leads
  const searchLeads = (query) => {
    if (!query) return state.leads;

    const lowercaseQuery = query.toLowerCase();
    return state.leads.filter(lead =>
      lead.firstName?.toLowerCase().includes(lowercaseQuery) ||
      lead.lastName?.toLowerCase().includes(lowercaseQuery) ||
      lead.email?.toLowerCase().includes(lowercaseQuery) ||
      lead.company?.toLowerCase().includes(lowercaseQuery) ||
      lead.phone?.includes(query)
    );
  };

  // Add a remark to a lead
  const addRemark = async (leadId, remark) => {
    try {
      const result = await leadService.addRemark(leadId, remark);
      return result;
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  // Clear error from context
  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  const value = {
    ...state,
    createLead,
    createQuickLead,
    updateLead,
    deleteLead,
    bulkDeleteLeads,
    bulkAssignLeads,
    getLeadById,
    getLeadsByStatus,
    getLeadsBySource,
    searchLeads,
    loadLeads,
    addRemark,
    clearError,
    leadStatuses,
    mapStageToStatusName
  };

  return (
    <LeadContext.Provider value={value}>
      {children}
    </LeadContext.Provider>
  );
};

// Custom hook to use lead context
export const useLeads = () => {
  const context = useContext(LeadContext);
  if (!context) {
    throw new Error('useLeads must be used within a LeadProvider');
  }
  return context;
};

export default LeadContext;
